/*!
 * packing.js - MaxRects packing engine for the Gang Sheet Builder.
 *
 * Replaces the old shelf packing: instead of fixed-height rows, the engine
 * tracks every remaining free rectangle of the bin (the "maximal rectangles"
 * scheme), so pieces can land in leftover holes and be rotated 90 degrees
 * when that uses the sheet better.
 *
 * The same engine powers four features of the app:
 *   - placing a new upload / duplicate in the first free spot,
 *   - Auto-arrange (repack everything, locked designs become obstacles),
 *   - "Fill sheet" (keep placing copies until nothing fits),
 *   - counting how many copies of a design still fit.
 *
 * Units are arbitrary (the app passes inches). Coordinates returned are
 * relative to the usable area handed to create(); the caller adds margins.
 *
 * Gap handling: every piece (and obstacle) is inflated by `gap` on its right
 * and bottom edges, and the bin is extended by `gap` on both axes. Two pieces
 * then always end up at least `gap` apart, while pieces may still sit flush
 * against the usable area's edges.
 *
 * Scoring is Bottom-Left (lowest y wins, then lowest x, then no rotation):
 * predictable "fill across, then down" behavior that matches how customers
 * read the sheet.
 *
 * Runs in the browser (window.Packing) and in Node (module.exports) for the
 * unit tests in tests/packing-test.cjs.
 */
(function (root, factory) {
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = factory();
  } else {
    root.Packing = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var EPS = 1e-7;

  function create(opts) {
    var gap = Math.max(0, opts.gap || 0);
    var binW = opts.width + gap;   // see gap handling note above
    var binH = opts.height + gap;
    if (!(opts.width > 0) || !(opts.height > 0)) {
      throw new Error('Packing: invalid bin ' + opts.width + 'x' + opts.height);
    }

    var free = [{ x: 0, y: 0, w: binW, h: binH }];

    function intersects(f, r) {
      return r.x < f.x + f.w - EPS && r.x + r.w > f.x + EPS &&
             r.y < f.y + f.h - EPS && r.y + r.h > f.y + EPS;
    }

    // Split every free rectangle that overlaps `r` into up to four smaller
    // maximal rectangles (the classic MaxRects split), then drop the ones
    // fully contained in another.
    function subtract(r) {
      var out = [];
      for (var i = 0; i < free.length; i++) {
        var f = free[i];
        if (!intersects(f, r)) { out.push(f); continue; }
        if (r.x > f.x + EPS) {                       // left slice
          out.push({ x: f.x, y: f.y, w: r.x - f.x, h: f.h });
        }
        if (r.x + r.w < f.x + f.w - EPS) {           // right slice
          out.push({ x: r.x + r.w, y: f.y, w: f.x + f.w - (r.x + r.w), h: f.h });
        }
        if (r.y > f.y + EPS) {                       // top slice
          out.push({ x: f.x, y: f.y, w: f.w, h: r.y - f.y });
        }
        if (r.y + r.h < f.y + f.h - EPS) {           // bottom slice
          out.push({ x: f.x, y: r.y + r.h, w: f.w, h: f.y + f.h - (r.y + r.h) });
        }
      }
      // prune rectangles contained in another (O(n^2), n stays small)
      var pruned = [];
      for (var a = 0; a < out.length; a++) {
        var ra = out[a];
        if (ra.w < EPS || ra.h < EPS) continue;
        var contained = false;
        for (var b = 0; b < out.length && !contained; b++) {
          if (a === b) continue;
          var rb = out[b];
          var sameAndLater = b > a &&
            Math.abs(ra.x - rb.x) < EPS && Math.abs(ra.y - rb.y) < EPS &&
            Math.abs(ra.w - rb.w) < EPS && Math.abs(ra.h - rb.h) < EPS;
          if (sameAndLater) contained = true; // exact duplicate: keep only one
          else if (ra.x >= rb.x - EPS && ra.y >= rb.y - EPS &&
                   ra.x + ra.w <= rb.x + rb.w + EPS &&
                   ra.y + ra.h <= rb.y + rb.h + EPS &&
                   (ra.w < rb.w - EPS || ra.h < rb.h - EPS ||
                    Math.abs(ra.x - rb.x) > EPS || Math.abs(ra.y - rb.y) > EPS)) {
            contained = true;
          }
        }
        if (!contained) pruned.push(ra);
      }
      free = pruned;
    }

    return {
      // Mark an already-occupied rectangle (an existing design on the sheet).
      // Coordinates may stick out of the bin (a design dragged into the
      // margin); the part inside is clipped and subtracted.
      occupy: function (x, y, w, h) {
        var x0 = Math.max(0, x), y0 = Math.max(0, y);
        var x1 = Math.min(binW, x + w + gap);
        var y1 = Math.min(binH, y + h + gap);
        if (x1 - x0 < EPS || y1 - y0 < EPS) return;
        subtract({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 });
      },

      // Find the Bottom-Left spot for a w x h piece, optionally trying the
      // piece rotated 90 degrees as well. Returns { x, y, rotated } or null,
      // and reserves the spot.
      place: function (w, h, allowRotate) {
        var iw = w + gap, ih = h + gap;
        var best = null;
        function consider(x, y, rot) {
          if (!best ||
              y < best.y - EPS ||
              (Math.abs(y - best.y) < EPS && x < best.x - EPS) ||
              (Math.abs(y - best.y) < EPS && Math.abs(x - best.x) < EPS && best.rot && !rot)) {
            best = { x: x, y: y, rot: rot };
          }
        }
        for (var i = 0; i < free.length; i++) {
          var f = free[i];
          if (iw <= f.w + EPS && ih <= f.h + EPS) consider(f.x, f.y, false);
          if (allowRotate && Math.abs(w - h) > EPS &&
              ih <= f.w + EPS && iw <= f.h + EPS) consider(f.x, f.y, true);
        }
        if (!best) return null;
        subtract({
          x: best.x, y: best.y,
          w: best.rot ? ih : iw,
          h: best.rot ? iw : ih
        });
        return { x: best.x, y: best.y, rotated: best.rot };
      },

      // exposed for tests/debugging
      _free: function () { return free.slice(); }
    };
  }

  return { create: create };
});
